import { Check, Sparkles } from "lucide-react";
import { useExchangeRate, toMxn } from "@/components/home/ExchangeRateProvider";
import { mxn, usd } from "@/lib/whatsapp";
import pricing from "@/content/pricing.json";

export const PlanCard = ({ plan, annual, onSelect, index }) => {
  const { rate } = useExchangeRate();
  const months = annual ? pricing.annual_months_charged : 1;
  const usdTotal = plan.usd_monthly * months;
  const usdEffective = annual ? usdTotal / 12 : plan.usd_monthly;
  const mxnEffective = toMxn(usdEffective, rate);
  const mxnTotal = toMxn(usdTotal, rate);
  const isFree = plan.usd_monthly === 0;

  return (
    <article
      className={`relative flex flex-col rounded-3xl p-6 lg:p-7 border transition-transform duration-300 hover:-translate-y-1 ${plan.highlight ? "bg-brand-carbon text-white border-brand-carbon shadow-[0_24px_60px_rgba(31,31,31,0.35)] lg:scale-[1.04]" : "bg-white border-orange-100 shadow-[0_10px_34px_rgba(255,102,18,0.08)]"}`}
      data-testid={`plan-card-${plan.id}`}
    >
      {plan.highlight && <span className="absolute -top-3.5 left-6 inline-flex items-center gap-1 bg-brand-orange text-white text-xs font-bold px-3 py-1.5 rounded-full"><Sparkles className="w-3.5 h-3.5" /> Más popular</span>}
      <p className={`text-xs font-semibold uppercase tracking-wider ${plan.highlight ? "text-brand-orange" : "text-gray-500"}`}>{plan.tagline}</p>
      <h3 className="mt-1 text-2xl">{plan.name}</h3>
      <p className={`mt-1 text-sm ${plan.highlight ? "text-gray-300" : "text-gray-500"}`}>{plan.ideal}</p>

      <div className="mt-5">
        <p className="flex items-end gap-1.5 leading-none">
          <span className="font-heading font-extrabold text-4xl lg:text-[44px]" data-testid={`plan-usd-${plan.id}`}>{usd(usdEffective)}</span>
          <span className={`text-sm font-semibold mb-1 ${plan.highlight ? "text-gray-300" : "text-gray-500"}`}>USD / mes</span>
        </p>
        <p className={`mt-2 font-semibold ${plan.highlight ? "text-brand-orange" : "text-brand-orange-dark"}`} data-testid={`plan-mxn-${plan.id}`}>
          {isFree ? "$0 MXN · gratis para siempre" : mxnEffective != null ? `≈ ${mxn(mxnEffective)} MXN / mes` : "≈ MXN según tipo de cambio del día"}
        </p>
        <p className={`mt-1 text-xs ${plan.highlight ? "text-gray-400" : "text-gray-500"}`} data-testid={`plan-billing-${plan.id}`}>
          {isFree ? plan.billing_note : annual ? `Se cobra ${usd(usdTotal)} USD al año${mxnTotal != null ? ` (≈ ${mxn(mxnTotal)} MXN)` : ""} · ${pricing.annual_discount_label}` : plan.billing_note}
        </p>
      </div>

      {plan.trial && (
        <span className={`mt-4 self-start text-xs font-bold px-3 py-1.5 rounded-full ${plan.highlight ? "bg-brand-green/20 text-green-300" : "bg-brand-green/10 text-brand-green"}`} data-testid={`plan-trial-${plan.id}`}>
          {pricing.trial_days} días de prueba gratis
        </span>
      )}

      <ul className={`mt-5 space-y-2.5 text-sm flex-1 ${plan.highlight ? "text-gray-200" : "text-gray-700"}`}>
        {plan.features.map((f) => (
          <li key={f} className="flex items-start gap-2.5"><Check className={`w-4 h-4 mt-0.5 shrink-0 ${plan.highlight ? "text-brand-orange" : "text-brand-green"}`} /> {f}</li>
        ))}
      </ul>

      <button onClick={() => onSelect(plan)} className={`btn-mt mt-6 !w-full ${plan.highlight ? "btn-mt-primary" : isFree ? "btn-mt-outline" : "btn-mt-primary"}`} data-track={plan.track} data-testid={`plan-cta-${plan.id}`} style={{ animationDelay: `${index * 60}ms` }}>
        {plan.cta}
      </button>
    </article>
  );
};

export default PlanCard;
